import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { useClientProfile } from '../context/ClientProfileContext';
import { useAmazonRegistry } from '../context/AmazonBrandRegistryContext';
import { useAmazonProducts } from '../context/AmazonProductsContext';
import VendrLayout from '../VendrLayout';
import FormSection from '../components/FormSection';
import FormField from '../components/FormField';
import './AmazonProductForm.css';

const BUCKET = 'client-portal-uploads';

const FULFILLMENT_OPTIONS = [
  { value: 'FBA', label: 'FBA (Fulfilled by Amazon)' },
  { value: 'FBM', label: 'FBM (Fulfilled by Merchant)' },
];

const emptyForm = {
  product_name: '',
  brand_name: '',
  product_category: '',
  product_description: '',
  bullet_point_1: '',
  bullet_point_2: '',
  bullet_point_3: '',
  bullet_point_4: '',
  bullet_point_5: '',
  search_terms: '',
  model_number: '',
  has_variations: null,
  variation_options: '',
  variation_structure: '',
  retail_price: '',
  map_price: '',
  cost_of_goods: '',
  inventory_quantity: '',
  fulfillment_method: '',
  country_of_origin: '',
  upc_ean_gtin: '',
  asin: '',
  sku: '',
  product_video_url: '',
  certifications: '',
  expiration_shelf_life: '',
  hazardous_materials: '',
  product_length: '',
  product_width: '',
  product_height: '',
  product_weight: '',
  packaging_dimensions: '',
  units_per_case: '',
  prep_labeling_requirements: '',
};

const FILE_FIELDS = [
  'main_image',
  'additional_image_1',
  'additional_image_2',
  'additional_image_3',
  'back_of_pack',
  'product_insert',
  'safety_data_sheet',
];

async function uploadProductFile(file, clientProfileId, productId, fieldName) {
  const ext = file.name.split('.').pop();
  const path = `${clientProfileId}/amazon-products/${productId}/${fieldName}.${ext}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: true });
  if (error) throw new Error(error.message);
  return path;
}

export default function AmazonProductForm() {
  const { id: productId } = useParams();
  const navigate = useNavigate();
  const { profile, loading: profileLoading } = useClientProfile();
  const { products, loading: productsLoading, saveProduct } = useAmazonProducts();

  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState(
    Object.fromEntries(FILE_FIELDS.map(f => [f, null]))
  );
  const [savedPaths, setSavedPaths] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { isAmazonEnabled, isAmazonSubmitted, loading: amazonLoading } = useAmazonRegistry();

  const isEditMode = !!productId;
  const existingProduct = isEditMode
    ? products.find(p => p.id === productId) ?? null
    : null;

  useEffect(() => {
    if (!existingProduct) return;
    setForm({
      product_name: existingProduct.product_name || '',
      brand_name: existingProduct.brand_name || '',
      product_category: existingProduct.product_category || '',
      product_description: existingProduct.product_description || '',
      bullet_point_1: existingProduct.bullet_point_1 || '',
      bullet_point_2: existingProduct.bullet_point_2 || '',
      bullet_point_3: existingProduct.bullet_point_3 || '',
      bullet_point_4: existingProduct.bullet_point_4 || '',
      bullet_point_5: existingProduct.bullet_point_5 || '',
      search_terms: existingProduct.search_terms || '',
      model_number: existingProduct.model_number || '',
      has_variations: existingProduct.has_variations,
      variation_options: existingProduct.variation_options || '',
      variation_structure: existingProduct.variation_structure || '',
      retail_price: existingProduct.retail_price ?? '',
      map_price: existingProduct.map_price ?? '',
      cost_of_goods: existingProduct.cost_of_goods ?? '',
      inventory_quantity: existingProduct.inventory_quantity ?? '',
      fulfillment_method: existingProduct.fulfillment_method || '',
      country_of_origin: existingProduct.country_of_origin || '',
      upc_ean_gtin: existingProduct.upc_ean_gtin || '',
      asin: existingProduct.asin || '',
      sku: existingProduct.sku || '',
      product_video_url: existingProduct.product_video_url || '',
      certifications: existingProduct.certifications || '',
      expiration_shelf_life: existingProduct.expiration_shelf_life || '',
      hazardous_materials: existingProduct.hazardous_materials || '',
      product_length: existingProduct.product_length ?? '',
      product_width: existingProduct.product_width ?? '',
      product_height: existingProduct.product_height ?? '',
      product_weight: existingProduct.product_weight ?? '',
      packaging_dimensions: existingProduct.packaging_dimensions || '',
      units_per_case: existingProduct.units_per_case ?? '',
      prep_labeling_requirements: existingProduct.prep_labeling_requirements || '',
    });
    setSavedPaths({
      main_image: existingProduct.main_image_path || '',
      additional_image_1: existingProduct.additional_image_1_path || '',
      additional_image_2: existingProduct.additional_image_2_path || '',
      additional_image_3: existingProduct.additional_image_3_path || '',
      back_of_pack: existingProduct.back_of_pack_path || '',
      product_insert: existingProduct.product_insert_path || '',
      safety_data_sheet: existingProduct.safety_data_sheet_path || '',
    });
  }, [existingProduct]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    const { name } = e.target;
    setFiles(prev => ({ ...prev, [name]: e.target.files[0] ?? null }));
  };

  const handleVariationRadio = value => {
    setForm(prev => ({ ...prev, has_variations: value }));
  };

  const handleFulfillmentRadio = value => {
    setForm(prev => ({ ...prev, fulfillment_method: value }));
  };

  const uploadAllFiles = async (resolvedProductId) => {
    const paths = {};
    for (const fieldName of FILE_FIELDS) {
      if (files[fieldName]) {
        paths[`${fieldName}_path`] = await uploadProductFile(
          files[fieldName],
          profile.id,
          resolvedProductId,
          fieldName
        );
      }
    }
    return paths;
  };

  const buildPayload = (filePaths) => ({
    ...form,
    retail_price: form.retail_price === '' ? null : Number(form.retail_price),
    map_price: form.map_price === '' ? null : Number(form.map_price),
    cost_of_goods: form.cost_of_goods === '' ? null : Number(form.cost_of_goods),
    inventory_quantity: form.inventory_quantity === '' ? null : Number(form.inventory_quantity),
    product_length: form.product_length === '' ? null : Number(form.product_length),
    product_width: form.product_width === '' ? null : Number(form.product_width),
    product_height: form.product_height === '' ? null : Number(form.product_height),
    product_weight: form.product_weight === '' ? null : Number(form.product_weight),
    units_per_case: form.units_per_case === '' ? null : Number(form.units_per_case),
    main_image_path: filePaths.main_image_path || savedPaths.main_image || null,
    additional_image_1_path: filePaths.additional_image_1_path || savedPaths.additional_image_1 || null,
    additional_image_2_path: filePaths.additional_image_2_path || savedPaths.additional_image_2 || null,
    additional_image_3_path: filePaths.additional_image_3_path || savedPaths.additional_image_3 || null,
    back_of_pack_path: filePaths.back_of_pack_path || savedPaths.back_of_pack || null,
    product_insert_path: filePaths.product_insert_path || savedPaths.product_insert || null,
    safety_data_sheet_path: filePaths.safety_data_sheet_path || savedPaths.safety_data_sheet || null,
  });

  const handleSaveDraft = async () => {
    if (!form.product_name.trim()) {
      setError('Product name is required to save a draft.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const resolvedId = isEditMode ? productId : null;
      const filePaths = await uploadAllFiles(resolvedId || 'temp-' + Date.now());
      const result = await saveProduct(buildPayload(filePaths), resolvedId, false);
      if (!result.success) throw new Error(result.error);
      navigate('/vendr');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.product_name.trim()) {
      setError('Product name is required.');
      return;
    }
    if (!form.brand_name.trim()) {
      setError('Brand name is required.');
      return;
    }
    if (!form.product_category.trim()) {
      setError('Product category is required.');
      return;
    }
    const hasMainImage = files.main_image || savedPaths.main_image;
    if (!hasMainImage) {
      setError('A main product image is required before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const resolvedId = isEditMode ? productId : null;
      const filePaths = await uploadAllFiles(resolvedId || 'temp-' + Date.now());
      const result = await saveProduct(buildPayload(filePaths), resolvedId, true);
      if (!result.success) throw new Error(result.error);
      navigate('/vendr');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (profileLoading || amazonLoading) {
    return (
      <VendrLayout>
        <div className="ap-form-loading">Loading…</div>
      </VendrLayout>
    );
  }

  if (!isAmazonEnabled || !isAmazonSubmitted) {
    return <Navigate to="/vendr" replace />;
  }

  if (isEditMode && productsLoading) {
    return (
      <VendrLayout>
        <div className="ap-form-loading">Loading…</div>
      </VendrLayout>
    );
  }

  const isSubmitted = existingProduct?.status === 'submitted';

  return (
    <VendrLayout>
      <div className="ap-form">
        <div className="ap-form-header">
          <h1>{isEditMode ? 'Edit Amazon Product' : 'Add Amazon Product'}</h1>
          <p>Fill in the details below so MarketVendr can create and optimise your Amazon listing.</p>
          {isSubmitted && (
            <div className="ap-form-submitted-banner">
              ✓ This product has been submitted.
            </div>
          )}
        </div>

        {/* 1. Product Details */}
        <FormSection title="Product Details">
          <FormField label="Product Name / Title" name="product_name" value={form.product_name} onChange={handleChange} required />
          <FormField label="Brand Name" name="brand_name" value={form.brand_name} onChange={handleChange} required />
          <FormField label="Product Category" name="product_category" value={form.product_category} onChange={handleChange} required />
          <FormField label="Model Number or Style Name" name="model_number" value={form.model_number} onChange={handleChange} optional />
          <FormField label="Product Description" name="product_description" inputType="textarea" rows={4} value={form.product_description} onChange={handleChange} fullWidth optional />
          <FormField label="Key Feature 1" name="bullet_point_1" value={form.bullet_point_1} onChange={handleChange} fullWidth optional />
          <FormField label="Key Feature 2" name="bullet_point_2" value={form.bullet_point_2} onChange={handleChange} fullWidth optional />
          <FormField label="Key Feature 3" name="bullet_point_3" value={form.bullet_point_3} onChange={handleChange} fullWidth optional />
          <FormField label="Key Feature 4" name="bullet_point_4" value={form.bullet_point_4} onChange={handleChange} fullWidth optional />
          <FormField label="Key Feature 5" name="bullet_point_5" value={form.bullet_point_5} onChange={handleChange} fullWidth optional />
          <FormField label="Search Terms / Keywords" name="search_terms" value={form.search_terms} onChange={handleChange} fullWidth optional helperText="Separate keywords with commas or spaces" />
        </FormSection>

        {/* 2. Variations */}
        <FormSection title="Variations">
          <div className="ap-form-radio-group ap-form-field-full">
            <p className="ap-form-radio-label">Does this product have variations? (Size, Colour, Flavour, etc.)</p>
            <label className="ap-form-radio-option">
              <input type="radio" name="has_variations" checked={form.has_variations === true} onChange={() => handleVariationRadio(true)} />
              Yes
            </label>
            <label className="ap-form-radio-option">
              <input type="radio" name="has_variations" checked={form.has_variations === false} onChange={() => handleVariationRadio(false)} />
              No
            </label>
          </div>
          {form.has_variations === true && (
            <>
              <FormField label="List all variation options" name="variation_options" inputType="textarea" rows={3} value={form.variation_options} onChange={handleChange} fullWidth placeholder="e.g. Small, Medium, Large / Red, Blue, Green" />
              <FormField label="Parent / Child structure details" name="variation_structure" inputType="textarea" rows={2} value={form.variation_structure} onChange={handleChange} fullWidth optional />
            </>
          )}
        </FormSection>

        {/* 3. Pricing & Inventory */}
        <FormSection title="Pricing & Inventory">
          <FormField label="Retail Price (MSRP)" name="retail_price" inputType="number" step="0.01" unitHint="$" value={form.retail_price} onChange={handleChange} optional />
          <FormField label="Minimum Advertised Price (MAP)" name="map_price" inputType="number" step="0.01" unitHint="$" value={form.map_price} onChange={handleChange} optional />
          <FormField label="Cost of Goods" name="cost_of_goods" inputType="number" step="0.01" unitHint="$" value={form.cost_of_goods} onChange={handleChange} optional />
          <FormField label="Initial Available Inventory Quantity" name="inventory_quantity" inputType="number" step="1" value={form.inventory_quantity} onChange={handleChange} optional />
          <FormField label="Country of Origin" name="country_of_origin" value={form.country_of_origin} onChange={handleChange} optional />
          <div className="ap-form-radio-group">
            <p className="ap-form-radio-label">Fulfillment Method</p>
            {FULFILLMENT_OPTIONS.map(opt => (
              <label key={opt.value} className="ap-form-radio-option">
                <input
                  type="radio"
                  name="fulfillment_method"
                  checked={form.fulfillment_method === opt.value}
                  onChange={() => handleFulfillmentRadio(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </FormSection>

        {/* 4. Product Identifiers */}
        <FormSection title="Product Identifiers">
          <FormField label="UPC, EAN, or GTIN" name="upc_ean_gtin" value={form.upc_ean_gtin} onChange={handleChange} optional />
          <FormField label="ASIN" name="asin" value={form.asin} onChange={handleChange} optional helperText="If already created" />
          <FormField label="SKU" name="sku" value={form.sku} onChange={handleChange} optional helperText="If already assigned" />
        </FormSection>

        {/* 5. Images & Media */}
        <FormSection title="Images & Media">
          <FormField
            label="Main Product Image"
            name="main_image"
            inputType="file"
            accept="image/*"
            onChange={handleFileChange}
            fileSaved={savedPaths.main_image ? 'Current image saved' : false}
            required
            helperText="Amazon-compliant white background required"
            fullWidth
          />
          <FormField label="Additional Image 1" name="additional_image_1" inputType="file" accept="image/*" onChange={handleFileChange} fileSaved={!!savedPaths.additional_image_1} optional />
          <FormField label="Additional Image 2" name="additional_image_2" inputType="file" accept="image/*" onChange={handleFileChange} fileSaved={!!savedPaths.additional_image_2} optional />
          <FormField label="Additional Image 3" name="additional_image_3" inputType="file" accept="image/*" onChange={handleFileChange} fileSaved={!!savedPaths.additional_image_3} optional />
          <FormField label="Back-of-Pack or Ingredient Image" name="back_of_pack" inputType="file" accept="image/*" onChange={handleFileChange} fileSaved={!!savedPaths.back_of_pack} optional />
          <FormField label="Product Insert or Packaging Design" name="product_insert" inputType="file" accept="image/*,application/pdf" onChange={handleFileChange} fileSaved={!!savedPaths.product_insert} optional />
          <FormField label="Product Video URL" name="product_video_url" inputType="url" value={form.product_video_url} onChange={handleChange} optional placeholder="https://" fullWidth />
        </FormSection>

        {/* 6. Compliance & Safety */}
        <FormSection title="Compliance & Safety">
          <FormField label="Required Certifications or Compliance Documents" name="certifications" inputType="textarea" rows={3} value={form.certifications} onChange={handleChange} fullWidth optional placeholder="e.g. FDA, FCC, CPSIA" />
          <FormField label="Safety Data Sheet" name="safety_data_sheet" inputType="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} fileSaved={!!savedPaths.safety_data_sheet} optional />
          <FormField label="Expiration Date or Shelf Life" name="expiration_shelf_life" value={form.expiration_shelf_life} onChange={handleChange} optional />
          <FormField label="Hazardous Materials Information" name="hazardous_materials" inputType="textarea" rows={2} value={form.hazardous_materials} onChange={handleChange} fullWidth optional />
        </FormSection>

        {/* 7. Logistics & Packaging */}
        <FormSection title="Logistics & Packaging">
          <FormField label="Length" name="product_length" inputType="number" step="0.001" unitHint="cm" value={form.product_length} onChange={handleChange} optional />
          <FormField label="Width" name="product_width" inputType="number" step="0.001" unitHint="cm" value={form.product_width} onChange={handleChange} optional />
          <FormField label="Height" name="product_height" inputType="number" step="0.001" unitHint="cm" value={form.product_height} onChange={handleChange} optional />
          <FormField label="Product Weight" name="product_weight" inputType="number" step="0.001" unitHint="kg" value={form.product_weight} onChange={handleChange} optional />
          <FormField label="Packaging Dimensions" name="packaging_dimensions" value={form.packaging_dimensions} onChange={handleChange} optional placeholder="L × W × H cm" />
          <FormField label="Units Per Case" name="units_per_case" inputType="number" step="1" value={form.units_per_case} onChange={handleChange} optional />
          <FormField label="Prep or Labeling Requirements" name="prep_labeling_requirements" inputType="textarea" rows={2} value={form.prep_labeling_requirements} onChange={handleChange} fullWidth optional />
        </FormSection>

        {error && <p className="ap-form-error">{error}</p>}

        <div className="ap-form-actions">
          <button
            type="button"
            className="ap-form-btn-draft"
            onClick={handleSaveDraft}
            disabled={saving || submitting}
          >
            {saving ? 'Saving…' : 'Save Draft'}
          </button>
          <button
            type="button"
            className="ap-form-btn-submit"
            onClick={handleSubmit}
            disabled={saving || submitting}
          >
            {submitting ? 'Submitting…' : 'Submit Product'}
          </button>
        </div>
      </div>
    </VendrLayout>
  );
}
